import React, { useState, useEffect } from 'react';
import { Plus, Trash, X, UsersThree, Sparkle } from '@phosphor-icons/react';
import { TagInput, EmptyState } from './Shared';
import { PREF_LEVELS, DAY_MOODS } from './constants';
import SmartFillModal from './SmartFillModal';
import LearnedProfileCard from './LearnedProfileCard';
import ImportItemsModal from './ImportItemsModal';
import JainCalendarSection from './JainCalendarSection';

export default function FoodProfileTab({ api, prefs, onUpdate }) {
  const [showItemsImport, setShowItemsImport] = useState(false);
  const [localPrefs, setLocalPrefs] = useState(prefs || {});
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('basics');
  const [newItem, setNewItem] = useState('');
  // Which Smart-Fill modal is open: null | 'allergies' | 'notes' | 'staff'
  const [smartFor, setSmartFor] = useState(null);

  useEffect(() => { if (prefs) setLocalPrefs(prefs); }, [prefs]);

  // Smart Fill — apply parsed fields back to the form. For list fields the user
  // picks merge/replace via the modal toggle; non-list fields always overwrite.
  const applySmart = ({ parsed, mode }) => {
    if (smartFor === 'allergies') {
      const incoming = parsed.allergies || [];
      let next;
      if (mode === 'replace') {
        next = incoming;
      } else {
        // merge dedupe (case-insensitive)
        const seen = new Set((localPrefs.allergies || []).map(s => String(s).toLowerCase().trim()));
        next = [...(localPrefs.allergies || [])];
        incoming.forEach(a => {
          const k = String(a).toLowerCase().trim();
          if (k && !seen.has(k)) { seen.add(k); next.push(a); }
        });
      }
      setLocalPrefs(p => ({ ...p, allergies: next }));
      save({ allergies: next });
    } else if (smartFor === 'notes') {
      const txt = parsed.notes || '';
      setLocalPrefs(p => ({ ...p, notes: txt }));
      save({ notes: txt });
    } else if (smartFor === 'staff') {
      const patch = {};
      if (parsed.staff_count != null) patch.staff_count = parsed.staff_count;
      if (parsed.staff_spice_level) patch.staff_spice_level = parsed.staff_spice_level;
      if (parsed.staff_diet_type !== null && parsed.staff_diet_type !== undefined) patch.staff_diet_type = parsed.staff_diet_type;
      if (parsed.staff_notes) patch.staff_notes = parsed.staff_notes;
      // If the user wasn't already in staff mode, enable it for them.
      if (Object.keys(patch).length > 0 && !localPrefs.has_staff_meals) patch.has_staff_meals = true;
      setLocalPrefs(p => ({ ...p, ...patch }));
      save(patch);
    }
    setSmartFor(null);
  };

  const save = async (updates) => {
    setSaving(true);
    try {
      await api.put('/meals/preferences', updates || localPrefs);
      onUpdate();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const addIngredientPref = (name, level) => {
    const updated = { ...localPrefs, ingredient_prefs: { ...localPrefs.ingredient_prefs, [name]: level } };
    setLocalPrefs(updated);
    save({ ingredient_prefs: updated.ingredient_prefs });
  };

  const addDishPref = (name, level) => {
    const updated = { ...localPrefs, dish_prefs: { ...localPrefs.dish_prefs, [name]: level } };
    setLocalPrefs(updated);
    save({ dish_prefs: updated.dish_prefs });
  };

  const removeIngredientPref = (name) => {
    const ip = { ...localPrefs.ingredient_prefs };
    delete ip[name];
    const updated = { ...localPrefs, ingredient_prefs: ip };
    setLocalPrefs(updated);
    save({ ingredient_prefs: ip });
  };

  const removeDishPref = (name) => {
    const dp = { ...localPrefs.dish_prefs };
    delete dp[name];
    const updated = { ...localPrefs, dish_prefs: dp };
    setLocalPrefs(updated);
    save({ dish_prefs: dp });
  };

  const addFrequencyRule = () => {
    if (!newItem.trim()) return;
    const rules = [...(localPrefs.frequency_rules || []), { item: newItem.trim(), frequency: '2x_week', meal_type: 'any' }];
    const updated = { ...localPrefs, frequency_rules: rules };
    setLocalPrefs(updated);
    save({ frequency_rules: rules });
    setNewItem('');
  };

  const removeFreqRule = (idx) => {
    const rules = localPrefs.frequency_rules.filter((_, i) => i !== idx);
    const updated = { ...localPrefs, frequency_rules: rules };
    setLocalPrefs(updated);
    save({ frequency_rules: rules });
  };

  const updateFreqRule = (idx, field, val) => {
    const rules = [...localPrefs.frequency_rules];
    rules[idx] = { ...rules[idx], [field]: val };
    const updated = { ...localPrefs, frequency_rules: rules };
    setLocalPrefs(updated);
    save({ frequency_rules: rules });
  };

  const addFamilyMember = () => {
    const members = [...(localPrefs.family_members_preferences || []), { name: '', preferences: '', restrictions: '' }];
    setLocalPrefs(p => ({ ...p, family_members_preferences: members }));
  };

  const updateMember = (idx, field, val) => {
    const members = [...localPrefs.family_members_preferences];
    members[idx] = { ...members[idx], [field]: val };
    setLocalPrefs(p => ({ ...p, family_members_preferences: members }));
  };

  const removeMember = (idx) => {
    const members = localPrefs.family_members_preferences.filter((_, i) => i !== idx);
    setLocalPrefs(p => ({ ...p, family_members_preferences: members }));
    save({ family_members_preferences: members });
  };

  const sections = [
    { id: 'basics', label: 'Basics' },
    { id: 'by_meal', label: 'By Meal' },
    { id: 'ingredients', label: 'Ingredients' },
    { id: 'dishes', label: 'Dishes' },
    { id: 'frequency', label: 'Frequency' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'jain', label: 'Jain Days' },
    { id: 'family', label: 'Family' },
  ];

  const ingPrefs = localPrefs.ingredient_prefs || {};
  const dishPrefs = localPrefs.dish_prefs || {};

  return (
    <div className="space-y-4">
      <div className="rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap" style={{ background: 'rgba(255,107,53,0.05)', border: '1px solid rgba(255,107,53,0.15)' }}>
        <div>
          <p className="text-xs font-semibold text-[#FF6B35]">Smart Food Profile</p>
          <p className="text-[11px] text-neutral-400 mt-0.5">The more you fill in, the smarter the AI Chef gets. It reads every preference when generating your weekly menu.</p>
        </div>
        <button onClick={() => setShowItemsImport(true)} data-testid="import-items-btn"
          className="px-3 py-2 rounded-xl text-xs font-bold border bg-amber-500/10 text-amber-200 border-amber-500/40 hover:bg-amber-500/20 flex-shrink-0">
          ⬆ Import Items List (Excel)
        </button>
      </div>
      {showItemsImport && <ImportItemsModal api={api} onClose={() => setShowItemsImport(false)} onUpdate={onUpdate} />}

      <LearnedProfileCard api={api} />

      <div className="flex gap-1 p-1 rounded-xl overflow-x-auto" style={{ background: 'rgba(255,255,255,0.02)' }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap flex-shrink-0 transition-all ${section === s.id ? 'bg-white/10 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {section === 'basics' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Diet Type</label>
              <select value={localPrefs.diet_type || ''} onChange={e => { setLocalPrefs(p => ({ ...p, diet_type: e.target.value })); save({ diet_type: e.target.value }); }}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" data-testid="diet-type">
                <option value="Jain Vegetarian">Jain Vegetarian</option>
                <option value="Vegetarian">Vegetarian (No Jain restrictions)</option>
                <option value="Vegan">Vegan</option>
                <option value="Eggetarian">Eggetarian</option>
                <option value="Non-Vegetarian">Non-Vegetarian</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Spice Level</label>
              <div className="flex gap-2">
                {['mild', 'medium', 'spicy', 'very_spicy'].map(s => (
                  <button key={s} onClick={() => { setLocalPrefs(p => ({ ...p, spice_level: s })); save({ spice_level: s }); }}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${localPrefs.spice_level === s ? 'ring-2 ring-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'bg-white/5 text-neutral-400'}`}>
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Cuisine Preferences</label>
            <div className="flex flex-wrap gap-2">
              {['Gujarati', 'North Indian', 'South Indian', 'Rajasthani', 'Punjabi', 'Marathi', 'Bengali', 'Continental', 'Chinese'].map(c => (
                <button key={c} onClick={() => {
                  const list = localPrefs.cuisine_preferences || [];
                  const updated = list.includes(c) ? list.filter(x => x !== c) : [...list, c];
                  setLocalPrefs(p => ({ ...p, cuisine_preferences: updated }));
                  save({ cuisine_preferences: updated });
                }}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${(localPrefs.cuisine_preferences || []).includes(c) ? 'bg-[#FF6B35]/15 text-[#FF6B35] ring-1 ring-[#FF6B35]/30' : 'bg-white/5 text-neutral-400'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Health Goals</label>
            <div className="flex flex-wrap gap-2">
              {['Low Oil', 'More Protein', 'Less Sugar', 'Low Carb', 'High Fiber', 'Weight Loss', 'Diabetic Friendly', 'Iron Rich'].map(g => (
                <button key={g} onClick={() => {
                  const list = localPrefs.health_goals || [];
                  const updated = list.includes(g) ? list.filter(x => x !== g) : [...list, g];
                  setLocalPrefs(p => ({ ...p, health_goals: updated }));
                  save({ health_goals: updated });
                }}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${(localPrefs.health_goals || []).includes(g) ? 'bg-green-500/15 text-green-400 ring-1 ring-green-500/30' : 'bg-white/5 text-neutral-400'}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-neutral-500 uppercase">Allergies</label>
              <button onClick={() => setSmartFor('allergies')} data-testid="smart-fill-allergies-btn"
                className="px-2 py-0.5 rounded-md text-[10px] font-bold inline-flex items-center gap-1"
                style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(236,72,153,0.20))', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.40)' }}>
                <Sparkle size={9} weight="fill" /> Smart fill
              </button>
            </div>
            <TagInput values={localPrefs.allergies || []} onChange={v => { setLocalPrefs(p => ({ ...p, allergies: v })); save({ allergies: v }); }} placeholder="Type allergy and press Enter" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-neutral-500 uppercase">Notes for AI</label>
              <button onClick={() => setSmartFor('notes')} data-testid="smart-fill-notes-btn"
                className="px-2 py-0.5 rounded-md text-[10px] font-bold inline-flex items-center gap-1"
                style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(236,72,153,0.20))', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.40)' }}>
                <Sparkle size={9} weight="fill" /> Polish with AI
              </button>
            </div>
            <textarea value={localPrefs.notes || ''} onChange={e => setLocalPrefs(p => ({ ...p, notes: e.target.value }))}
              onBlur={() => save({ notes: localPrefs.notes })}
              placeholder="e.g., We prefer less oil cooking, kids like cheese..."
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none resize-none h-20" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Kitchen Language (for Cook)</label>
            <div className="flex gap-2">
              {[{ val: 'hi', label: 'Hindi', desc: 'Cook ko Hindi mein bhejein' }, { val: 'en', label: 'English', desc: 'Send to cook in English' }].map(l => (
                <button key={l.val} onClick={() => { setLocalPrefs(p => ({ ...p, kitchen_language: l.val })); save({ kitchen_language: l.val }); }}
                  data-testid={`kitchen-lang-${l.val}`}
                  className={`flex-1 p-3 rounded-xl text-left transition-all ${(localPrefs.kitchen_language || 'hi') === l.val ? 'ring-2 ring-[#FF6B35] bg-[#FF6B35]/10' : 'bg-white/5 hover:bg-white/[0.07]'}`}>
                  <p className={`text-sm font-semibold ${(localPrefs.kitchen_language || 'hi') === l.val ? 'text-[#FF6B35]' : 'text-neutral-300'}`}>{l.label}</p>
                  <p className="text-[11px] text-neutral-500 mt-0.5">{l.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Phase 75b — staple items whitelist. Used by smart staff sharing to
              deterministically mark a slot as "staff shares" when EVERY family
              dish is in this list. Catches household-specific staples the LLM
              might not recognise (e.g., Khichiyu, Khakhra Vagharela). */}
          <div className="rounded-2xl p-4" data-testid="staple-items-card"
            style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.20)' }}>
            <div className="mb-2">
              <p className="text-sm font-bold text-white">My staple items</p>
              <p className="text-[11px] text-neutral-400 mt-0.5 leading-snug">
                Smart staff sharing uses this whitelist: when EVERY dish in a meal matches
                one of these, staff automatically shares — no AI guesswork. Add your
                household&apos;s everyday items (Khichiyu, Khakhra Vagharela, Aloo Sabzi, etc.).
              </p>
            </div>
            <div data-testid="staple-items-input">
              <TagInput values={localPrefs.staple_items || []}
                onChange={v => { setLocalPrefs(p => ({ ...p, staple_items: v })); save({ staple_items: v }); }}
                placeholder="Type a staple and press Enter" />
            </div>
          </div>

          <div className="rounded-2xl p-4" style={{ background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.20)' }}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex-1">
                <p className="text-sm font-bold text-white flex items-center gap-2">
                  <UsersThree size={14} weight="bold" className="text-purple-300" />
                  Cooking staff
                </p>
                <p className="text-[11px] text-neutral-400 mt-0.5">How does cooking staff eat at your home?</p>
              </div>
              <button onClick={() => setSmartFor('staff')} data-testid="smart-fill-staff-btn"
                className="px-2 py-0.5 rounded-md text-[10px] font-bold inline-flex items-center gap-1 flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.20), rgba(236,72,153,0.20))', color: '#E9D5FF', border: '1px solid rgba(168,85,247,0.40)' }}>
                <Sparkle size={9} weight="fill" /> Smart fill
              </button>
            </div>

            {/* Sharing mode picker (Phase 75) — replaces the old single boolean toggle. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-testid="staff-sharing-mode-picker">
              {[
                { id: 'smart', label: 'Smart (recommended)', desc: 'Staff eats with family when meal is a staple; separate when variety.' },
                { id: 'always_share', label: 'Always share', desc: 'Staff always eats what family eats.' },
                { id: 'always_separate', label: 'Always separate', desc: 'Always generate a separate staff menu.' },
              ].map(m => {
                const active = (localPrefs.staff_sharing_mode || (localPrefs.has_staff_meals ? 'always_separate' : 'smart')) === m.id;
                return (
                  <button key={m.id} type="button"
                    onClick={() => {
                      const patch = { staff_sharing_mode: m.id, has_staff_meals: m.id !== 'always_share' };
                      setLocalPrefs(p => ({ ...p, ...patch }));
                      save(patch);
                    }}
                    data-testid={`staff-mode-${m.id}`}
                    className={`p-2.5 rounded-xl text-left transition-all border ${active ? 'bg-purple-500/15 border-purple-500/50 ring-1 ring-purple-400/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                    <p className={`text-[11px] font-bold ${active ? 'text-purple-200' : 'text-neutral-300'}`}>{m.label}</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5 leading-snug">{m.desc}</p>
                  </button>
                );
              })}
            </div>
            {/* Hidden legacy toggle kept for older test cases — actual control above. */}
            <input type="hidden" data-testid="has-staff-meals-toggle" value={localPrefs.has_staff_meals ? 'true' : 'false'} />
            <input type="hidden" data-testid="has-staff-meals-checkbox" value={localPrefs.has_staff_meals ? 'true' : 'false'} />

            {/* Staff details only relevant when not 'always_share' (we still need cook info either way for shopping). */}
            {(localPrefs.staff_sharing_mode || (localPrefs.has_staff_meals ? 'always_separate' : 'smart')) !== 'always_share' && localPrefs.has_staff_meals && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">Staff count</label>
                  <input type="number" min="1" max="20" value={localPrefs.staff_count || 1}
                    onChange={e => { const v = parseInt(e.target.value, 10) || 1; setLocalPrefs(p => ({ ...p, staff_count: v })); save({ staff_count: v }); }}
                    data-testid="staff-count-input"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">Spice level</label>
                  <select value={localPrefs.staff_spice_level || 'mild'}
                    onChange={e => { setLocalPrefs(p => ({ ...p, staff_spice_level: e.target.value })); save({ staff_spice_level: e.target.value }); }}
                    data-testid="staff-spice-select"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-purple-500">
                    <option value="mild">Mild</option>
                    <option value="medium">Medium</option>
                    <option value="hot">Hot</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">Diet (optional override)</label>
                  <select value={localPrefs.staff_diet_type || ''}
                    onChange={e => { setLocalPrefs(p => ({ ...p, staff_diet_type: e.target.value })); save({ staff_diet_type: e.target.value }); }}
                    data-testid="staff-diet-select"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-purple-500">
                    <option value="">Same as family</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Non-Vegetarian">Non-Vegetarian</option>
                    <option value="Jain Vegetarian">Jain Vegetarian</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">Notes</label>
                  <input type="text" value={localPrefs.staff_notes || ''}
                    onChange={e => setLocalPrefs(p => ({ ...p, staff_notes: e.target.value }))}
                    onBlur={() => save({ staff_notes: localPrefs.staff_notes || '' })}
                    placeholder="e.g. prefer simple dal-roti, no paneer, no curd"
                    data-testid="staff-notes-input"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-purple-500 placeholder:text-neutral-600" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {section === 'by_meal' && (
        <div className="space-y-4" data-testid="by-meal-section">
          <div className="rounded-2xl p-3" style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.20)' }}>
            <p className="text-[12px] text-emerald-300/90">
              <b>Per meal-type rotation</b> — tell the AI Chef what you usually eat at each meal slot.
              Empty list = no preference (AI picks freely). Each card also has an &quot;AI may suggest varieties&quot; toggle.
            </p>
          </div>

          {[
            { id: 'breakfast', label: 'Breakfast', field: 'breakfast_preferred_items', avoidField: null, varKey: 'breakfast',
              placeholder: 'e.g., Poha, Upma, Idli, Uttapam, Khakra Vagharela, Khichiyu', tip: 'Items rotate at breakfast.' },
            { id: 'lunch', label: 'Lunch', field: 'lunch_preferred_items', avoidField: null, varKey: 'lunch',
              placeholder: 'e.g., Roti, Sabzi, Dal, Rice', tip: 'Default staples. Eaters with diet goals (Weight Loss / Low Carb / Diabetic) get a lighter alternative automatically.' },
            { id: 'dinner', label: 'Dinner', field: 'dinner_preferred_items', avoidField: 'dinner_avoid_items', varKey: 'dinner',
              placeholder: 'e.g., Salad, Soup, Continental, Pasta', tip: 'Variety at dinner. List items to AVOID below.' },
            { id: 'hi_tea', label: 'Hi-tea / Evening snack', field: 'hi_tea_preferred_items', avoidField: null, varKey: 'hi_tea',
              placeholder: 'e.g., Vada Pav, Sandwich, Pakoda', tip: 'Light bites. AI can suggest new ideas too.' },
          ].map(card => {
            const preferred = localPrefs[card.field] || [];
            const avoid = card.avoidField ? (localPrefs[card.avoidField] || []) : null;
            const aiVar = (localPrefs.ai_suggest_varieties_per_meal || {})[card.varKey];
            const aiVarOn = aiVar === undefined ? true : !!aiVar;
            return (
              <div key={card.id} className="rounded-2xl p-3 sm:p-4"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid={`bymeal-card-${card.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-bold text-white">{card.label}</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">{card.tip}</p>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">Preferred items</label>
                  <div data-testid={`bymeal-preferred-${card.id}`}>
                    <TagInput values={preferred}
                      onChange={v => { setLocalPrefs(p => ({ ...p, [card.field]: v })); save({ [card.field]: v }); }}
                      placeholder={card.placeholder} />
                  </div>
                </div>

                {avoid !== null && (
                  <div className="mb-3">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">Avoid at this meal</label>
                    <div data-testid={`bymeal-avoid-${card.id}`}>
                      <TagInput values={avoid}
                        onChange={v => { setLocalPrefs(p => ({ ...p, [card.avoidField]: v })); save({ [card.avoidField]: v }); }}
                        placeholder="e.g., Roti, Sabzi" />
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer" data-testid={`bymeal-ai-suggest-${card.id}`}>
                  <input type="checkbox" checked={aiVarOn}
                    onChange={e => {
                      const next = { ...(localPrefs.ai_suggest_varieties_per_meal || {}), [card.varKey]: e.target.checked };
                      setLocalPrefs(p => ({ ...p, ai_suggest_varieties_per_meal: next }));
                      save({ ai_suggest_varieties_per_meal: next });
                    }}
                    className="accent-emerald-500" />
                  <span className="text-[11px] text-neutral-300">
                    <span className="text-emerald-300 font-bold">AI may suggest varieties</span> — let the chef add similar new dishes beyond your list
                  </span>
                </label>

                {/* Phase 85 — Hard cap on dish count per slot */}
                {(() => {
                  const slotKey = card.varKey === 'hi_tea' ? 'snacks' : card.varKey;
                  const defaults = { breakfast: 3, lunch: 4, dinner: 4, snacks: 2 };
                  const ips = localPrefs.items_per_slot || {};
                  const cur = ips[slotKey] != null ? ips[slotKey] : defaults[slotKey];
                  const setCount = (n) => {
                    const next = { ...(localPrefs.items_per_slot || {}), [slotKey]: n };
                    setLocalPrefs(p => ({ ...p, items_per_slot: next }));
                    save({ items_per_slot: next });
                  };
                  return (
                    <div className="mt-2 pt-2 border-t border-white/5">
                      <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">
                        Max items per {card.label.toLowerCase()} · <span className="text-neutral-400 normal-case tracking-normal">cook prepares this many</span>
                      </label>
                      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`bymeal-items-count-${card.id}`}>
                        {[1, 2, 3, 4, 5, 6].map(n => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setCount(n)}
                            data-testid={`bymeal-items-count-${card.id}-${n}`}
                            className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-all ${cur === n
                              ? 'bg-[#FF6B35]/25 text-[#FF6B35] border-[#FF6B35]/50'
                              : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white hover:border-white/25'}`}
                          >
                            {n} {n === 1 ? 'item' : 'items'}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-neutral-500 mt-1 italic">
                        AI Chef will return EXACTLY {cur} item{cur === 1 ? '' : 's'} for this slot in every generated menu.
                      </p>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
      {section === 'ingredients' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input value={newItem} onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) { addIngredientPref(newItem.trim(), 'love'); setNewItem(''); } }}
              placeholder="Add ingredient (e.g., Paneer, Moong Dal...)" data-testid="add-ingredient"
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            <button onClick={() => { if (newItem.trim()) { addIngredientPref(newItem.trim(), 'love'); setNewItem(''); } }}
              className="px-4 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold flex-shrink-0">Add</button>
          </div>
          {Object.keys(ingPrefs).length === 0 ? (
            <EmptyState text="No ingredient preferences set. Add ingredients and rate them." />
          ) : (
            <div className="space-y-2">
              {PREF_LEVELS.map(level => {
                const items = Object.entries(ingPrefs).filter(([_, v]) => v === level.val);
                if (items.length === 0) return null;
                return (
                  <div key={level.val}>
                    <p className={`text-[10px] uppercase font-bold tracking-wider mb-1.5 ${level.color.split(' ')[0]}`}>{level.label} ({items.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map(([name]) => (
                        <div key={name} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <span>{name}</span>
                          <select value={ingPrefs[name]} onChange={e => addIngredientPref(name, e.target.value)}
                            className="bg-transparent text-[10px] outline-none cursor-pointer text-neutral-500 w-12">
                            {PREF_LEVELS.map(l => <option key={l.val} value={l.val}>{l.label}</option>)}
                          </select>
                          <button onClick={() => removeIngredientPref(name)} className="text-neutral-600 hover:text-red-400"><X size={10} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {section === 'dishes' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input value={newItem} onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) { addDishPref(newItem.trim(), 'love'); setNewItem(''); } }}
              placeholder="Add dish (e.g., Paneer Tikka, Khichdi...)" data-testid="add-dish"
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            <button onClick={() => { if (newItem.trim()) { addDishPref(newItem.trim(), 'love'); setNewItem(''); } }}
              className="px-4 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold flex-shrink-0">Add</button>
          </div>
          {Object.keys(dishPrefs).length === 0 ? (
            <EmptyState text="No dish preferences set. Add dishes and rate them. AI also learns from your meal ratings." />
          ) : (
            <div className="space-y-2">
              {PREF_LEVELS.map(level => {
                const items = Object.entries(dishPrefs).filter(([_, v]) => v === level.val);
                if (items.length === 0) return null;
                return (
                  <div key={level.val}>
                    <p className={`text-[10px] uppercase font-bold tracking-wider mb-1.5 ${level.color.split(' ')[0]}`}>{level.label} ({items.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map(([name]) => (
                        <div key={name} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <span>{name}</span>
                          <select value={dishPrefs[name]} onChange={e => addDishPref(name, e.target.value)}
                            className="bg-transparent text-[10px] outline-none cursor-pointer text-neutral-500 w-12">
                            {PREF_LEVELS.map(l => <option key={l.val} value={l.val}>{l.label}</option>)}
                          </select>
                          <button onClick={() => removeDishPref(name)} className="text-neutral-600 hover:text-red-400"><X size={10} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {section === 'frequency' && (
        <div className="space-y-4">
          <p className="text-xs text-neutral-400">Set how often specific items should appear in your menu.</p>
          <div className="flex gap-2">
            <input value={newItem} onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addFrequencyRule(); }}
              placeholder="Item name (e.g., Paneer, Dal, Rice...)" data-testid="add-freq-item"
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            <button onClick={addFrequencyRule} className="px-4 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold flex-shrink-0">Add</button>
          </div>
          {(localPrefs.frequency_rules || []).length === 0 ? (
            <EmptyState text="No frequency rules. Add items like 'Paneer: 3x/week' or 'Rice: dinner only'." />
          ) : (
            <div className="space-y-2">
              {(localPrefs.frequency_rules || []).map((rule, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 rounded-xl flex-wrap" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <span className="text-sm font-semibold min-w-[80px]">{rule.item}</span>
                  <select value={rule.frequency} onChange={e => updateFreqRule(idx, 'frequency', e.target.value)}
                    className="px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs outline-none">
                    <option value="daily">Daily</option>
                    <option value="3x_week">3x/week</option>
                    <option value="2x_week">2x/week</option>
                    <option value="1x_week">1x/week</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="avoid">Avoid</option>
                  </select>
                  <select value={rule.meal_type} onChange={e => updateFreqRule(idx, 'meal_type', e.target.value)}
                    className="px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs outline-none">
                    <option value="any">Any meal</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                  </select>
                  <button onClick={() => removeFreqRule(idx)} className="p-1 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-red-400 ml-auto"><Trash size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === 'schedule' && (
        <div className="space-y-4">
          <p className="text-xs text-neutral-400">Set preferences for each day and meal type. AI will respect these when generating menus.</p>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-2">Day-of-Week Mood</label>
            <div className="space-y-2">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <div key={day} className="flex items-center gap-3">
                  <span className="text-sm w-24 capitalize">{day}</span>
                  <select value={(localPrefs.day_preferences || {})[day] || ''} onChange={e => {
                    const dp = { ...(localPrefs.day_preferences || {}), [day]: e.target.value };
                    setLocalPrefs(p => ({ ...p, day_preferences: dp }));
                    save({ day_preferences: dp });
                  }} className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs outline-none flex-1">
                    {DAY_MOODS.map(m => <option key={m} value={m}>{m ? m.replace('_', ' ') : '-- no preference --'}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-2">Meal Type Notes</label>
            <div className="space-y-3">
              {['breakfast', 'lunch', 'dinner', 'snacks'].map(mt => (
                <div key={mt}>
                  <span className="text-xs capitalize text-neutral-400 block mb-1">{mt}</span>
                  <input value={(localPrefs.meal_type_notes || {})[mt] || ''} onChange={e => {
                    const mn = { ...(localPrefs.meal_type_notes || {}), [mt]: e.target.value };
                    setLocalPrefs(p => ({ ...p, meal_type_notes: mn }));
                  }}
                    onBlur={() => save({ meal_type_notes: localPrefs.meal_type_notes })}
                    placeholder={`e.g., ${mt === 'breakfast' ? 'Quick items on weekdays, heavy on Sunday' : mt === 'dinner' ? 'Light meals preferred' : 'Any notes...'}`}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {section === 'jain' && <JainCalendarSection api={api} />}

      {section === 'family' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-400">Individual family member preferences</p>
            <button onClick={addFamilyMember} className="px-3 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-bold flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          {(localPrefs.family_members_preferences || []).length === 0 ? (
            <EmptyState text="No family-specific preferences. Add members with their individual food needs." />
          ) : (
            <div className="space-y-3">
              {(localPrefs.family_members_preferences || []).map((m, idx) => (
                <div key={idx} className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="flex items-center gap-2">
                    <input value={m.name} onChange={e => updateMember(idx, 'name', e.target.value)}
                      onBlur={() => save({ family_members_preferences: localPrefs.family_members_preferences })}
                      placeholder="Name" className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
                    <button onClick={() => removeMember(idx)} className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-red-400"><Trash size={14} /></button>
                  </div>
                  <input value={m.preferences || ''} onChange={e => updateMember(idx, 'preferences', e.target.value)}
                    onBlur={() => save({ family_members_preferences: localPrefs.family_members_preferences })}
                    placeholder="Preferences (e.g., loves cheese, more paneer)" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs outline-none" />
                  <input value={m.restrictions || ''} onChange={e => updateMember(idx, 'restrictions', e.target.value)}
                    onBlur={() => save({ family_members_preferences: localPrefs.family_members_preferences })}
                    placeholder="Restrictions (e.g., no spicy, no sweets)" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs outline-none" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {saving && <p className="text-[10px] text-[#FF6B35] animate-pulse">Saving...</p>}

      {smartFor === 'allergies' && (
        <SmartFillModal api={api} target="allergies"
          title="Smart fill — Allergies"
          placeholder="e.g., Everyone avoids garlic. Kids cannot have peanuts. My wife is lactose intolerant."
          examples={[
            "Everyone avoids garlic. Kids cannot have peanuts.",
            "My wife is lactose intolerant, we all avoid shellfish.",
            "No gluten for me, no eggs for the kids.",
          ]}
          context={{ existing_allergies: localPrefs.allergies || [] }}
          showMergeMode={true}
          onApply={applySmart}
          onClose={() => setSmartFor(null)} />
      )}
      {smartFor === 'notes' && (
        <SmartFillModal api={api} target="notes_polish"
          title="Polish notes with AI"
          placeholder="Paste your rough notes — AI will rewrite them into a clean, AI-friendly summary."
          examples={[
            (localPrefs.notes || '').slice(0, 200) || "Less oil; kids love cheese; husband prefers Indian; rotis 4x/week",
          ].filter(s => s && s.length > 5)}
          onApply={applySmart}
          onClose={() => setSmartFor(null)} />
      )}
      {smartFor === 'staff' && (
        <SmartFillModal api={api} target="staff_config"
          title="Smart fill — Cooking staff"
          placeholder="e.g., We have 3 helpers from UP, vegetarian, eat mild dal-roti-sabzi twice daily"
          examples={[
            "3 helpers from UP, vegetarian, mild spice, eat dal-roti-sabzi twice daily",
            "2 cooks, eat with us — just lower spice level",
            "1 helper, non-veg, prefers fish curry and rice",
          ]}
          onApply={applySmart}
          onClose={() => setSmartFor(null)} />
      )}
    </div>
  );
}
